import React from "react";
import { Box } from "@showitinc/showit-wc";

import styles from "./Modal.module.css";

/**
 * @typedef {Object} TabAction
 * @property {string} label - Label for the tab action button
 * @property {Function} onClick - Click handler for the tab action
 */

/**
 * @typedef {Object} Tab
 * @property {string} id - Unique identifier for the tab
 * @property {string} label - Display label for the tab
 */

/**
 * ContentPanel component that handles tabbed modal content with optional properties panel
 * @param {Function} mainContent - Function that returns the main content to display
 * @param {Function} propertiesPanel - Function that returns the properties panel content
 * @param {Object.<string, TabAction>} tabActions - Map of tab IDs to their associated actions
 * @param {React.ReactNode} children - Child components to render
 * @param {Tab[]} tabs - Array of tab objects defining the available tabs
 * @param {Tab[]} tabChildren - Array of child tab objects for the selected parent tab
 * @param {Function} onSelectTab - Handler called when a tab is selected
 * @param {Function} onSelectChildTab - Handler called when a child tab is selected
 * @param {string} selectedTab - ID of currently selected tab
 * @param {string} selectedChildTab - ID of currently selected child tab
 * @returns {React.ReactElement} Rendered ContentPanel component
 */

const ContentPanel = ({
  mainContent,
  propertiesPanel,
  tabActions = {},
  children,
  tabs,
  tabChildren,
  onSelectTab,
  onSelectChildTab,
  selectedTab,
  selectedChildTab,
}) => {
  const hasChildTabs = tabChildren?.length > 0;

  if (!tabs.length) {
    return (
      <div className={styles.content}>
        <div className={styles.mainContent}>{mainContent || children}</div>
        {propertiesPanel && <div className={styles.propertiesPanel}>{propertiesPanel}</div>}
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <Box className={styles.sideNav}>
        <div className={styles.tabList}>
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`${styles.tabSection} ${
                selectedTab === tab.id && hasChildTabs ? styles.tabHasChildren : ""
              }`}
            >
              <div
                onClick={() => onSelectTab(tab.id)}
                data-e2e={`ui-modal-tab-${tab.id}`}
                data-active={selectedTab === tab.id}
                className={`${styles.tab} ${selectedTab === tab.id ? styles.tabActive : ""} ${
                  selectedTab === tab.id && hasChildTabs ? styles.tabHasChildren : ""
                }`}
              >
                {tab.label}
              </div>
              {selectedTab === tab.id && hasChildTabs && (
                <div className={styles.childTabList}>
                  {tabChildren?.map((tabChild) => (
                    <div
                      onClick={() => onSelectChildTab(tabChild.id, tab.id)}
                      key={tabChild.id}
                      data-testid={`settings-tab-${tabChild.id}`}
                      className={`${styles.childTab} ${
                        selectedChildTab === tabChild.id ? styles.tabActive : ""
                      }`}
                    >
                      {tabChild.label}
                    </div>
                  ))}
                  {/* Tab-specific action at bottom of nav */}
                  {tabActions[selectedTab] && (
                    <div
                      onClick={tabActions[selectedTab].onClick}
                      className={`${styles.childTab} ${styles.actionsTab}`}
                    >
                      {tabActions[selectedTab].label}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Box>

      <div className={styles.mainContent}>
        {mainContent?.(selectedTab) || children?.[selectedTab] || children}
      </div>
      {propertiesPanel && propertiesPanel(selectedTab) && (
        <div className={styles.propertiesPanel}>
          {typeof propertiesPanel === "function" ? propertiesPanel(selectedTab) : propertiesPanel}
        </div>
      )}
    </div>
  );
};

export default ContentPanel;
